import { effect } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import type { Dataset } from '@life/data';
import type { Life } from '@life/engine';
import { t } from '@life/i18n';
import { applyTheme, resolveTheme } from './theme';
import type { LoadError } from './state/boot';
import type { Clock } from './state/clock';
import type { Game } from './state/game';
import { StartScreen } from './screens/StartScreen';
import { BirthScreen } from './screens/BirthScreen';
import { LifeScreen } from './screens/LifeScreen';
import { DeathScreen } from './screens/DeathScreen';
import { DossierScreen } from './screens/DossierScreen';
import { downloadBlob, renderBirthCard } from './share/card';

export interface AppProps {
  game: Game;
  ds: Dataset;
  /** Сохранение не прочитано при старте — стартовый экран покажет сообщение. */
  loadError?: LoadError | null;
  /** Часы экрана жизни (инъекция для тестов). */
  clock?: Clock;
}

/**
 * Корень клиента: роутер по `game.screen`: start | birth | life | death | dossier
 * («Личное дело» поверх жизни, H1).
 * Карточка рождения для шеринга рендерится здесь и скачивается как PNG.
 *
 * Сохранение: `effect` подписан на жизнь, настройки, полку и экран, так что
 * каждый тик, ответ на решение, пометка в журнале, эпитафия и смена темы
 * пишутся в localStorage сразу (persist глотает ошибки хранилища); открытая
 * мини-игра и её сыгранный исход — тоже (Review Focus 3 плана 2b).
 *
 * Полка: «Свидетельство» на старте открывает завершённую жизнь в `DeathScreen`
 * режима просмотра (`viewing`), стор при этом не трогается — «Назад» возвращает
 * на старт.
 */
async function shareBirth(life: Life, ds: Dataset): Promise<void> {
  try {
    downloadBlob(await renderBirthCard(life, ds), t('ui.share.file.birth'));
  } catch {
    // Нет canvas (старый браузер или приватный режим) — кнопка просто ничего не делает.
  }
}

export function App({ game, ds, loadError = null, clock }: AppProps) {
  const [viewing, setViewing] = useState<Life | null>(null);
  applyTheme(resolveTheme(game.settings.value.theme));
  useEffect(
    () =>
      effect(() => {
        // Чтение сигналов — подписка; значения не нужны.
        void game.life.value;
        void game.settings.value;
        void game.shelf.value;
        void game.screen.value;
        void game.pendingMinigame.value;
        void game.minigameQueued.value;
        game.persist();
      }),
    [game],
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = () => applyTheme(resolveTheme(game.settings.value.theme));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [game]);

  switch (game.screen.value) {
    case 'birth':
      return <BirthScreen game={game} ds={ds} onShare={(life) => void shareBirth(life, ds)} />;
    case 'life':
      return clock ? <LifeScreen game={game} ds={ds} clock={clock} /> : <LifeScreen game={game} ds={ds} />;
    case 'death':
      return <DeathScreen game={game} ds={ds} />;
    case 'dossier':
      return <DossierScreen game={game} ds={ds} />;
    default:
      if (viewing !== null) {
        return <DeathScreen game={game} ds={ds} life={viewing} readonly onBack={() => setViewing(null)} />;
      }
      return <StartScreen game={game} ds={ds} loadError={loadError} onCertificate={setViewing} />;
  }
}
